import React, { useState, useCallback } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  createStyles,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { SeasonViewQuery, useDeleteQuestMutation, SeasonViewDocument } from '../../../../../schema';
import { QuestModal, QuestTypeSelectModal, DeleteQuestModal, Quest, QuestType } from './QuestModal';
import { QuestsTable } from './QuestsTable';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    emptyMessage: {
      textAlign: 'center',
      color: theme.palette.text.secondary,
      padding: theme.spacing(4),
    },
  })
);

type Props = {
  data: SeasonViewQuery | undefined;
  seasonId: string;
};

export const QuestsList = ({ data, seasonId }: Props) => {
  const classes = useStyles();
  const quests = data?.season?.quests || [];

  const [deleteQuest, { loading: deleteLoading }] = useDeleteQuestMutation({
    refetchQueries: [{ query: SeasonViewDocument, variables: { id: seasonId } }],
  });

  const [typeSelectOpen, setTypeSelectOpen] = useState(false);
  const [questModalOpen, setQuestModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedQuest, setSelectedQuest] = useState<Quest | null>(null);
  const [selectedQuestType, setSelectedQuestType] = useState<QuestType>('SubscribeToWardQuest');

  const handleAddClick = useCallback(() => {
    setTypeSelectOpen(true);
  }, []);

  const handleTypeSelect = useCallback((type: QuestType) => {
    setSelectedQuestType(type);
    setTypeSelectOpen(false);
    setSelectedQuest(null);
    setQuestModalOpen(true);
  }, []);

  const handleEditQuest = useCallback((quest: Quest) => {
    setSelectedQuest(quest);
    setSelectedQuestType(quest.__typename as QuestType);
    setQuestModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((quest: Quest) => {
    setSelectedQuest(quest);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseTypeSelect = useCallback(() => {
    setTypeSelectOpen(false);
  }, []);

  const handleCloseQuestModal = useCallback(() => {
    setQuestModalOpen(false);
    setSelectedQuest(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedQuest(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedQuest) return;

    try {
      await deleteQuest({
        variables: {
          input: { id: selectedQuest.id },
        },
      });
      toast.success('Задание успешно удалено');
      handleCloseDeleteModal();
    } catch (error) {
      toast.error('Ошибка при удалении задания');
    }
  }, [selectedQuest, deleteQuest, handleCloseDeleteModal]);

  return (
    <>
      <Card style={{ marginTop: 16 }}>
        <CardHeader
          title="Задания"
          action={
            <Button color="primary" variant="contained" size="small" onClick={handleAddClick}>
              Добавить задание
            </Button>
          }
        />
        <CardContent>
          {quests.length === 0 ? (
            <Typography className={classes.emptyMessage}>Задания не добавлены</Typography>
          ) : (
            <QuestsTable
              quests={quests}
              onEditQuest={handleEditQuest}
              onDeleteQuest={handleDeleteClick}
            />
          )}
        </CardContent>
      </Card>

      <QuestTypeSelectModal
        open={typeSelectOpen}
        onSelect={handleTypeSelect}
        onClose={handleCloseTypeSelect}
      />

      <QuestModal
        open={questModalOpen}
        seasonId={seasonId}
        quest={selectedQuest}
        questType={selectedQuestType}
        onClose={handleCloseQuestModal}
      />

      <DeleteQuestModal
        open={deleteModalOpen}
        questTitle={selectedQuest?.title || ''}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteModal}
        loading={deleteLoading}
      />
    </>
  );
};
